// src/components/Tours.js
import React, { useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Link } from "react-router-dom";
import L from "leaflet";

const availableTours = [
  {
    name: "Downtown City Walk",
    price: 20,
    currency: "USD",
    duration: "3–4 hrs",
    image: "/images/Citywalk1.jpeg",
    tag: "Most Popular",
    tagColor: "var(--gold)",
    desc: "Experience Nairobi's heartbeat — walk through its culture, markets, and history.",
    details:
      "Anyone can see Nairobi from a car window — but only by walking do you hear the heartbeat of the city. Perfect for travellers who want to see, taste, and feel Nairobi the way locals do.",
    path: "/city-walk",
  },
  {
    name: "Historical & Cultural Tour",
    price: 80,
    currency: "USD",
    duration: "3-4 hrs",
    image: "/images/Hist1.jpeg",
    tag: "Cultural",
    tagColor: "#7C9E6B",
    desc: "Discover Kenya's past and living traditions — a journey through history and heritage.",
    details:
      "Explore Kenya's history at the National Museum, the colonial Karen Blixen Farmhouse, and enjoy living traditions at Bomas of Kenya.",
    path: "/historical-cultural",
  },
  {
    name: "Kibera Slum Tour",
    price: 30,
    currency: "USD",
    duration: "3 hrs",
    image: "/images/Ki1.png",
    tag: "Community",
    tagColor: "#9E7B4E",
    desc: "Step into the vibrant heart of Nairobi's Kibera — a community of strength and creativity.",
    details:
      "Walk with local guides, visit small enterprises, and support community-led initiatives in one of Africa's most talked-about neighbourhoods.",
    path: "/slum-tours",
  },
  {
    name: "Naivasha Excursion",
    price: 120,
    currency: "USD",
    duration: "Full Day",
    image: "/images/Naiv1.jpeg",
    tag: "Adventure",
    tagColor: "#4E7A9E",
    desc: "Discover Lake Naivasha's beauty — boat rides, hippos, and Hell's Gate adventures.",
    details:
      "Boat ride spotting hippos and fish eagles, walk among giraffes at Crescent Island, and explore Hell's Gate National Park.",
    path: "/excursions",
    location: "Naivasha",
  },
  {
    name: "Nairobi Wildlife Excursion",
    price: 100,
    currency: "USD",
    duration: "4-5 hrs",
    image: "/images/Nai1.jpeg",
    tag: "Wildlife",
    tagColor: "#6B7C4A",
    desc: "Feed giraffes, meet baby elephants, and explore Nairobi's wildlife in one day.",
    details:
      "Feed giraffes, meet baby elephants, and explore Nairobi Safari Walk — a full-day wildlife adventure for conservation lovers.",
    path: "/excursions",
    location: "Nairobi",
  },
];

// Fix Leaflet default icon

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function Tours() {
  const position = [-1.2862300150364285, 36.82271491566195];
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleRouteClick = () => {
    window.open(
      `https://www.google.com/maps/dir/?api=1&destination=${position[0]},${position[1]}`,
      "_blank"
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        :root {
          --gold: #C9A84C;
          --gold-light: #E8C97A;
          --dark: #0A0A0A;
          --charcoal: #141414;
        }

        .tours-root {
          font-family: 'Jost', sans-serif;
          background: var(--dark);
          color: #fff;
          min-height: 100vh;
        }

        /* ── HERO ── */
        .tours-hero {
          background: linear-gradient(160deg, #0d0b06 0%, #1a1500 55%, #0A0A0A 100%);
          border-bottom: 1px solid rgba(201,168,76,0.2);
          padding: 5rem 1.5rem 3.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .tours-hero::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 70% 60% at 50% 100%, rgba(201,168,76,0.06) 0%, transparent 70%);
          pointer-events: none;
        }
        .tours-eyebrow {
          font-size: 0.68rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.8rem;
        }
        .tours-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.6rem, 6vw, 4.2rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.08;
          margin-bottom: 0.4rem;
        }
        .tours-title em { font-style: italic; color: var(--gold-light); }
        .tours-divider {
          width: 50px;
          height: 2px;
          background: var(--gold);
          margin: 1.2rem auto 1.5rem;
        }
        .tours-subtitle {
          font-size: clamp(0.88rem, 2vw, 1rem);
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          max-width: 560px;
          margin: 0 auto;
          line-height: 1.85;
        }

        /* ── BODY ── */
        .tours-body {
          max-width: 1140px;
          margin: 0 auto;
          padding: 5rem 1.5rem 6rem;
        }

        /* Section header */
        .section-eyebrow {
          font-size: 0.65rem;
          letter-spacing: 5px;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.6rem;
        }
        .section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.4rem;
        }
        .section-line {
          width: 40px;
          height: 2px;
          background: var(--gold);
          margin-bottom: 3rem;
        }

        /* ── MAP CARD ── */
        .map-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.18);
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 5rem;
        }
        .map-card-header {
          padding: 2rem 2rem 1.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .map-card-header h3 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.3rem;
        }
        .map-card-header p {
          font-size: 0.85rem;
          color: rgba(255,255,255,0.45);
          margin: 0;
        }
        .map-card-header span {
          color: var(--gold);
          font-weight: 500;
        }
        .map-wrapper {
          position: relative;
          height: 320px;
        }
        .map-wrapper .leaflet-container {
          height: 100%;
          width: 100%;
          filter: grayscale(20%) brightness(0.85);
        }
        .map-card-footer {
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 1rem;
          border-top: 1px solid rgba(255,255,255,0.06);
        }
        .map-info {
          font-size: 0.82rem;
          color: rgba(255,255,255,0.4);
        }
        .map-info strong { color: rgba(255,255,255,0.7); font-weight: 500; }
        .btn-gold-route {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: var(--gold);
          color: #0A0A0A;
          border: none;
          padding: 0.7rem 1.8rem;
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-weight: 600;
          font-size: 0.8rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 0.25s, transform 0.2s;
        }
        .btn-gold-route:hover {
          background: var(--gold-light);
          transform: translateY(-2px);
        }

        /* ── TOUR CARDS ── */
        .tour-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(201,168,76,0.13);
          border-radius: 10px;
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
          transition: border-color 0.35s, transform 0.35s, box-shadow 0.35s;
          cursor: default;
        }
        .tour-card:hover {
          border-color: rgba(201,168,76,0.5);
          transform: translateY(-6px);
          box-shadow: 0 24px 60px rgba(0,0,0,0.5);
        }

        .tour-card-img {
          position: relative;
          height: 220px;
          overflow: hidden;
          flex-shrink: 0;
        }
        .tour-card-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s ease;
        }
        .tour-card:hover .tour-card-img img {
          transform: scale(1.06);
        }
        .tour-card-img::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 60px;
          background: linear-gradient(to top, rgba(10,10,10,0.8), transparent);
        }

        .tour-tag {
          position: absolute;
          top: 14px;
          left: 14px;
          font-size: 0.6rem;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          padding: 0.28rem 0.75rem;
          border-radius: 50px;
          z-index: 2;
          background: rgba(0,0,0,0.65);
          border: 1px solid;
          backdrop-filter: blur(4px);
        }

        .tour-card-body {
          padding: 1.6rem 1.5rem;
          display: flex;
          flex-direction: column;
          flex: 1;
        }
        .tour-meta {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.8rem;
        }
        .tour-meta-item {
          font-size: 0.68rem;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
        }
        .tour-meta-item span { color: var(--gold); margin-right: 0.3rem; }

        .tour-card-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.4rem;
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.6rem;
          line-height: 1.2;
        }
        .tour-card-desc {
          font-size: 0.85rem;
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          line-height: 1.75;
          margin-bottom: 1.2rem;
          flex: 1;
        }
        .tour-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top: 1px solid rgba(255,255,255,0.07);
          padding-top: 1.2rem;
          margin-top: auto;
        }
        .tour-price {
          display: flex;
          flex-direction: column;
        }
        .tour-price-from {
          font-size: 0.6rem;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.3);
        }
        .tour-price-amount {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.6rem;
          font-weight: 700;
          color: var(--gold-light);
          line-height: 1;
        }
        .tour-price-per {
          font-size: 0.65rem;
          color: rgba(255,255,255,0.3);
        }
        .tour-actions {
          display: flex;
          gap: 0.5rem;
        }
        .btn-tour-details {
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 1rem;
          border: 1px solid rgba(201,168,76,0.3);
          border-radius: 4px;
          color: rgba(255,255,255,0.6);
          font-size: 0.75rem;
          font-weight: 500;
          letter-spacing: 1px;
          text-transform: uppercase;
          text-decoration: none;
          font-family: 'Jost', sans-serif;
          transition: border-color 0.25s, color 0.25s;
        }
        .btn-tour-details:hover {
          border-color: var(--gold);
          color: var(--gold);
        }
        .btn-tour-book {
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 1.2rem;
          background: var(--gold);
          border: none;
          border-radius: 4px;
          color: #0A0A0A;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
          text-decoration: none;
          font-family: 'Jost', sans-serif;
          transition: background 0.25s, transform 0.2s;
        }
        .btn-tour-book:hover {
          background: var(--gold-light);
          transform: translateY(-1px);
          color: #0A0A0A;
        }

        /* ── BOTTOM CTA ── */
        .tours-cta {
          border-top: 1px solid rgba(201,168,76,0.15);
          background: var(--charcoal);
          padding: 4rem 1.5rem;
          text-align: center;
        }
        .tours-cta h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.8rem, 4vw, 2.8rem);
          font-weight: 700;
          color: #fff;
          margin-bottom: 0.8rem;
        }
        .tours-cta p {
          font-size: 0.9rem;
          color: rgba(255,255,255,0.45);
          max-width: 480px;
          margin: 0 auto 2rem;
          line-height: 1.8;
        }
        .btn-gold-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: transparent;
          border: 1px solid var(--gold);
          color: var(--gold);
          padding: 0.8rem 2.2rem;
          border-radius: 4px;
          font-family: 'Jost', sans-serif;
          font-weight: 600;
          font-size: 0.8rem;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          text-decoration: none;
          transition: background 0.25s, color 0.25s;
        }
        .btn-gold-cta:hover {
          background: var(--gold);
          color: #0A0A0A;
        }

        /* Separator */
        .gold-rule {
          border: none;
          border-top: 1px solid rgba(201,168,76,0.15);
          margin: 0;
        }

        @media (max-width: 767px) {
          .map-card-footer { flex-direction: column; align-items: flex-start; }
          .tour-card-img { height: 200px; }
        }
      `}</style>

      <div className="tours-root">

        {/* ── HERO ── */}
        <div className="tours-hero">
          <p className="tours-eyebrow">Nairobi's Ultimate Experience</p>
          <h1 className="tours-title">Our <em>Tours</em></h1>
          <div className="tours-divider" />
          <p className="tours-subtitle">
            Five distinct ways to experience Nairobi — each one crafted with
            precision, local insight, and an eye for the unforgettable.
          </p>
        </div>

        {/* ── BODY ── */}
        <div className="tours-body">

          {/* Map */}
          <div>
            <p className="section-eyebrow">Find Us</p>
            <h2 className="section-title">Starting Location</h2>
            <div className="section-line" />
          </div>

          <div className="map-card">
            <div className="map-card-header">
              <h3>Pickup Point</h3>
              <p>
                <span>Wabera Street</span> — just outside Carrefour St. Ellis, Nairobi CBD
              </p>
            </div>
            <div className="map-wrapper">
              <MapContainer center={position} zoom={16} style={{ height: "100%", width: "100%" }} zoomControl={false}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <Marker position={position}>
                  <Popup>
                    <strong>Nairobi's Ultimate Experience</strong><br />
                    Wabera St, outside Carrefour St. Ellis
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
            <div className="map-card-footer">
              <p className="map-info">
                <strong>Tip:</strong> Arrive 10 minutes early. Look for your guide holding the NUE sign.
              </p>
              <button className="btn-gold-route" onClick={handleRouteClick}>
                ↗ Navigate on Google Maps
              </button>
            </div>
          </div>

          {/* Tour Cards */}
          <div>
            <p className="section-eyebrow">Choose Your Journey</p>
            <h2 className="section-title">Available Tours</h2>
            <div className="section-line" />
          </div>

          <div className="row g-4">
            {availableTours.map((tour, i) => (
              <div key={i} className="col-12 col-sm-6 col-lg-4">
                <div
                  className="tour-card"
                  onMouseEnter={() => setHoveredCard(i)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Image */}
                  <div className="tour-card-img">
                    <img src={tour.image} alt={tour.name} loading="lazy" />
                    <span
                      className="tour-tag"
                      style={{ color: tour.tagColor, borderColor: tour.tagColor }}
                    >
                      {tour.tag}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="tour-card-body">
                    <div className="tour-meta">
                      <span className="tour-meta-item"><span>⏱</span>{tour.duration}</span>
                      <span className="tour-meta-item"><span>📍</span>{tour.location || 'Nairobi'}</span>
                    </div>
                    <h3 className="tour-card-name">{tour.name}</h3>
                    <p className="tour-card-desc">{tour.desc}</p>

                    {/* Footer */}
                    <div className="tour-card-footer">
                      <div className="tour-price">
                        <span className="tour-price-from">From</span>
                        <span className="tour-price-amount">${tour.price}</span>
                        <span className="tour-price-per">per person</span>
                      </div>
                      <div className="tour-actions">
                        <Link to={tour.path} className="btn-tour-details">Details</Link>
                        <Link to="/login" className="btn-tour-book">Book</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="tours-cta">
          <h2>Not Sure Which Tour?</h2>
          <p>
            Let us help you find the perfect experience based on your interests,
            time, and budget. We'll craft something just for you.
          </p>
          <a href="/contact" className="btn-gold-cta">Talk to Us</a>
        </div>

      </div>
    </>
  );
}

export default Tours;
